/**
 * API 辅助函数
 *
 * 提供 API 响应判断、安全执行等通用工具。
 * 从 types/api.ts 迁移而来，types/ 目录应仅包含类型定义。
 */

import type { ApiResponse } from '../types/api';

/**
 * 判断 API 响应是否成功
 *
 * 仅通过 `code` 与 `successCode` 比较来判断，不做隐式回退。
 * 如果后端使用 `0` 作为成功码，请传 `successCode: 0`。
 *
 * @param response - API 响应对象
 * @param successCode - 成功状态码，默认 '200'
 * @returns 是否成功
 *
 * @example
 * ```ts
 * const res = await fetchUser(1);
 * if (isApiSuccess(res)) {
 *   console.log(res.data);
 * }
 *
 * // 后端使用 0 作为成功码
 * if (isApiSuccess(res, 0)) { ... }
 * ```
 */
export function isApiSuccess<T>(
  response: ApiResponse<T> | null | undefined,
  successCode: string | number = '200',
): boolean {
  if (!response) return false;
  const codeStr = String(response.code ?? response.errorCode);
  return codeStr === String(successCode);
}

/**
 * 安全地执行 API 调用并处理错误
 *
 * @param apiFn - API 调用函数
 * @param options - 配置选项
 * @returns API 响应数据或 null
 *
 * @example
 * ```ts
 * // 获取数据
 * const data = await executeApiAction(
 *   () => fetchUser(1),
 *   {
 *     onSuccess: (data, msg) => message.success(msg),
 *     onError: (msg) => message.error(msg),
 *   }
 * );
 *
 * // 执行操作并判断成功
 * const data = await executeApiAction(
 *   () => deleteUser(id),
 *   {
 *     successMsg: 'Deleted',
 *     onSuccess: (data, msg) => message.success(msg),
 *     onError: (msg) => message.error(msg),
 *   }
 * );
 * if (data !== null) actionRef.current?.reload();
 * ```
 */
export async function executeApiAction<T>(
  apiFn: () => Promise<ApiResponse<T>>,
  options?: {
    /** 成功时的提示消息 */
    successMsg?: string;
    /** 失败时的提示消息 (覆盖 API 返回的 msg) */
    errorMsg?: string;
    /** 成功回调 */
    onSuccess?: (data: T, msg: string) => void;
    /** 错误回调 */
    onError?: (msg: string, code: string | number) => void;
    /** 成功状态码 */
    successCode?: string | number;
    /** 默认失败提示，默认 'Operation failed' */
    defaultErrorMsg?: string;
    /** 默认网络错误提示，默认 'Network error' */
    defaultNetworkErrorMsg?: string;
  },
): Promise<T | null> {
  const defaultErrorMsg = options?.defaultErrorMsg ?? 'Operation failed';
  const defaultNetworkErrorMsg =
    options?.defaultNetworkErrorMsg ?? 'Network error';

  try {
    const response = await apiFn();
    const successCode = options?.successCode ?? '200';

    if (isApiSuccess(response, successCode)) {
      const successMessage =
        options?.successMsg ??
        response.msg ??
        response.message ??
        response.errorMessage ??
        'Success';
      options?.onSuccess?.(response.data, successMessage);
      return response.data;
    }

    const errorMessage =
      options?.errorMsg ??
      response.msg ??
      response.message ??
      response.errorMessage ??
      defaultErrorMsg;
    const errCode = response.code ?? response.errorCode ?? 'UNKNOWN_ERROR';
    options?.onError?.(errorMessage, errCode);
    return null;
  } catch (error) {
    const errorMessage =
      options?.errorMsg ??
      (error instanceof Error ? error.message : defaultNetworkErrorMsg);
    options?.onError?.(errorMessage, 'NETWORK_ERROR');
    return null;
  }
}
